import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  Card,
  CardContent,
  CardMedia,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Rating,
} from "@mui/material";
import {
  Close,
  ArrowForward,
  CheckCircle,
  Phone,
  Email,
  Hardware,
  Favorite,
  BatteryChargingFull,
  Wifi,
  Tablet,
  Watch,
  Devices,
  Speed,
  Storage,
  CameraAlt,
  SignalCellularAlt,
  Language,
  SportsScore,
} from "@mui/icons-material";
import styles from "./Hardware.module.css";

// Products Data
const productsData = [
  {
    id: 1,
    name: "WaveSmartPulse",
    category: "Wearable",
    image:
      "https://images.unsplash.com/photo-1579586337278-3befd40fd17a?w=600&h=400&fit=crop",
    icon: <Watch sx={{ fontSize: 40 }} />,
    description:
      "A computerized wristwatch with functionality that goes beyond timekeeping. Helps users to keep track of their health with a combination of new features like heart rate and blood pressure monitor with pedometer.",
    features: [
      "Dynamic Heart Rate & Blood pressure Monitor",
      "Pedometer",
      "Calories consumed",
      "Distance travelled",
      "Sleep monitor",
      "Call/SMS/APP notification",
      "Stop watch",
      "Sedentary/alarm/anti-lost alert",
      "Wrist sense",
      "Find phone",
      "Remote camera",
      "Time/date/battery display",
    ],
    color: "#8cc61d",
  },
  {
    id: 2,
    name: "Wavefone",
    category: "Smartphone",
    image:
      "https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=600&h=400&fit=crop",
    icon: <Devices sx={{ fontSize: 40 }} />,
    description:
      "A unique smartphone developed by wavelength consultancy services with a choice of three different models to pick from and branding services including custom designed boot up information.",
    features: [
      "Android operating system",
      "Dual SIM",
      "Dual/Octa core processor",
      "Front/Back super camera",
      "Touchscreen",
      "2GB RAM",
    ],
    color: "#4a90d9",
  },
  {
    id: 3,
    name: "WaveStream Wi-Fi",
    category: "Network",
    image:
      "https://images.unsplash.com/photo-1544198365-f5d60b6d8190?w=600&h=400&fit=crop",
    icon: <Wifi sx={{ fontSize: 40 }} />,
    description:
      "A content provider and transmitter with antennas, and GPS. Multi-functional product designed exclusively to transmit pre-loaded programmes and/or curriculums. Compatible with wavepalm tablets, all other Android devices and Apple devices.",
    features: [
      "Movies",
      "Product & travail videos",
      "Advertisements",
      "Free wi-fi",
      "GPS",
      "Multi – language",
      "Support web browser app",
    ],
    color: "#9b59b6",
  },
  {
    id: 4,
    name: "Wavepalm Tablets",
    category: "Tablet",
    image:
      "https://images.unsplash.com/photo-1561154464-82e9adf32764?w=600&h=400&fit=crop",
    icon: <Tablet sx={{ fontSize: 40 }} />,
    description:
      "Carefully designed to meet your everyday need and suitable for a multiple range of users. Can be integrated with bespoke software for a range of sectors including education, medical, sports and entertainment.",
    features: [
      "Dual/octa core processor",
      "2 – 4 GB RAM & 32 – 64GB storage memory",
      "Clear LCD",
      "Wi-Fi & 4G or Wi-Fi only",
      "Back/front camera",
    ],
    color: "#f39c12",
  },
  {
    id: 5,
    name: "WaveSolar Power Bank",
    category: "Accessory",
    image:
      "https://images.unsplash.com/photo-1585338107529-13afc5f02586?w=600&h=400&fit=crop",
    icon: <BatteryChargingFull sx={{ fontSize: 40 }} />,
    description:
      "A power bank that can be used to charge any android and IOS phones or tablets. Portable and available in black, green, blue and orange colours.",
    features: [
      "Waterproof and anti-drop",
      "Li-polymer battery",
      "Capacity: 5000mAh",
      "Input: 5V/1A",
      "Output: 5V/2X1A",
      "Solar panel: 5.5V/1.2W",
      "AC charging time is about 5-6 hours",
    ],
    color: "#e74c3c",
  },
];

// Category Filters
const categories = [
  { id: "all", label: "All Products", icon: "📱" },
  { id: "Wearable", label: "Wearable", icon: "⌚" },
  { id: "Smartphone", label: "Smartphones", icon: "📱" },
  { id: "Network", label: "Network", icon: "📡" },
  { id: "Tablet", label: "Tablets", icon: "📟" },
  { id: "Accessory", label: "Accessories", icon: "🔋" },
];

// Stats Data
const statsData = [
  { number: "5+", label: "Products", icon: <Hardware /> },
  { number: "10+", label: "Features per Product", icon: <Speed /> },
  { number: "4.8", label: "Average Rating", icon: <Favorite /> },
  { number: "1000+", label: "Units Sold", icon: <SportsScore /> },
];

const Hardwares = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Filter products based on selected category
  const filteredProducts =
    selectedCategory === "all"
      ? productsData
      : productsData.filter((product) => product.category === selectedCategory);

  const handleOpenDialog = (product) => {
    setSelectedProduct(product);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedProduct(null);
  };

  return (
    <Box className={styles.hardwarePage}>
      {/* ===== HERO SECTION WITH BACKGROUND IMAGE & ANIMATIONS ===== */}
      <Box className={styles.heroSection}>
        {/* Animated Background Elements */}
        <Box className={styles.animatedBg}>
          {/* Floating Particles */}
          <Box className={`${styles.particle} ${styles.particle1}`}></Box>
          <Box className={`${styles.particle} ${styles.particle2}`}></Box>
          <Box className={`${styles.particle} ${styles.particle3}`}></Box>
          <Box className={`${styles.particle} ${styles.particle4}`}></Box>
          <Box className={`${styles.particle} ${styles.particle5}`}></Box>
          <Box className={`${styles.particle} ${styles.particle6}`}></Box>
          <Box className={`${styles.particle} ${styles.particle7}`}></Box>
          <Box className={`${styles.particle} ${styles.particle8}`}></Box>

          {/* Floating Hardware Icons */}
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon1}`}>
            🔧
          </Box>
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon2}`}>
            ⚙️
          </Box>
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon3}`}>
            📱
          </Box>
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon4}`}>
            💻
          </Box>
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon5}`}>
            🔌
          </Box>
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon6}`}>
            📡
          </Box>
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon7}`}>
            ⌚
          </Box>
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon8}`}>
            📟
          </Box>

          {/* Floating Shapes */}
          <Box className={`${styles.shape} ${styles.shape1}`}>
            <Box className={styles.shapeInner}></Box>
          </Box>
          <Box className={`${styles.shape} ${styles.shape2}`}>
            <Box className={styles.shapeInner}></Box>
          </Box>
          <Box className={`${styles.shape} ${styles.shape3}`}>
            <Box className={styles.shapeInner}></Box>
          </Box>

          {/* Floating Lines */}
          <Box className={`${styles.line} ${styles.line1}`}></Box>
          <Box className={`${styles.line} ${styles.line2}`}></Box>

          {/* Circuit Board Pattern */}
          <Box className={styles.circuitPattern}>
            <Box className={styles.circuitLine}></Box>
            <Box className={styles.circuitLine}></Box>
            <Box className={styles.circuitLine}></Box>
            <Box className={styles.circuitLine}></Box>
            <Box className={styles.circuitLine}></Box>
            <Box className={styles.circuitLine}></Box>
            <Box className={styles.circuitLine}></Box>
            <Box className={styles.circuitLine}></Box>
            <Box className={styles.circuitLine}></Box>
            <Box className={styles.circuitLine}></Box>
            <Box className={styles.circuitLine}></Box>
            <Box className={styles.circuitLine}></Box>
          </Box>
        </Box>

        <Container maxWidth="lg">
          <Box className={styles.heroContent}>
            <Typography
              variant="h1"
              className={styles.heroTitle}
              sx={{
                fontSize: { xs: "2.5rem", sm: "3.5rem", md: "4rem" },
              }}
            >
              Cutting Edge <span className={styles.highlight}>Technology</span>
            </Typography>
            <Typography variant="h6" className={styles.heroSubtitle}>
              We develop cutting edge technology products which improve and add
              value to real people whilst reducing related costs.
            </Typography>

            {/* Scroll Indicator */}
            <Box className={styles.scrollIndicator}>
              <Box className={styles.scrollMouse}>
                <Box className={styles.scrollWheel}></Box>
              </Box>
              <Typography variant="caption" className={styles.scrollText}>
                Scroll to explore
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box className={styles.statsSection}>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {statsData.map((stat, index) => (
              <Grid size={{ xs: 6, sm: 6, md: 3 }} key={index}>
                <Paper elevation={0} className={styles.statCard}>
                  <Box className={styles.statIcon}>{stat.icon}</Box>
                  <Typography variant="h3" className={styles.statNumber}>
                    {stat.number}
                  </Typography>
                  <Typography variant="body2" className={styles.statLabel}>
                    {stat.label}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Products Section */}
      <Box className={styles.productsSection}>
        <Container maxWidth="lg">
          <Box className={styles.sectionHeader}>
            <Chip
              label="📦 Our Products"
              className={styles.sectionChip}
              size="small"
            />
            <Typography
              variant="h2"
              className={styles.sectionTitle}
              sx={{
                fontSize: { xs: "2rem", sm: "2.5rem", md: "2.8rem" },
              }}
            >
              Innovative <span className={styles.highlight}>Hardware</span>
            </Typography>
            <Typography variant="body1" className={styles.sectionSubtitle}>
              Explore our range of cutting edge technology products
            </Typography>
          </Box>

          {/* Category Filters */}
          <Box className={styles.categoryFilters}>
            {categories.map((category) => (
              <button
                key={category.id}
                className={`${styles.categoryBtn} ${
                  selectedCategory === category.id
                    ? styles.categoryBtnActive
                    : ""
                }`}
                onClick={() => setSelectedCategory(category.id)}
              >
                <span className={styles.categoryIcon}>{category.icon}</span>
                {category.label}
              </button>
            ))}
          </Box>

          {/* Products Grid */}
          <Grid container spacing={4}>
            {filteredProducts.map((product) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={product.id}>
                <Card className={styles.productCard}>
                  <Box className={styles.cardImageWrapper}>
                    <CardMedia
                      component="img"
                      image={product.image}
                      alt={product.name}
                      className={styles.cardImage}
                    />
                    <Box className={styles.cardBadge}>
                      <Chip
                        label={product.category}
                        size="small"
                        className={styles.categoryBadge}
                        sx={{
                          background: `${product.color}20`,
                          color: product.color,
                        }}
                      />
                    </Box>
                    <Box className={styles.cardIcon}>{product.icon}</Box>
                  </Box>
                  <CardContent className={styles.cardContent}>
                    <Typography variant="h5" className={styles.cardTitle}>
                      {product.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      className={styles.cardDescription}
                    >
                      {product.description.length > 120
                        ? `${product.description.substring(0, 120)}...`
                        : product.description}
                    </Typography>
                    <Box className={styles.cardFeatures}>
                      {product.features.slice(0, 4).map((feature, index) => (
                        <Box key={index} className={styles.featureChip}>
                          <CheckCircle className={styles.featureCheck} />
                          <Typography variant="caption">{feature}</Typography>
                        </Box>
                      ))}
                      {product.features.length > 4 && (
                        <Typography
                          variant="caption"
                          className={styles.moreFeatures}
                        >
                          +{product.features.length - 4} more features
                        </Typography>
                      )}
                    </Box>
                    <Button
                      variant="contained"
                      className={styles.viewBtn}
                      onClick={() => handleOpenDialog(product)}
                      endIcon={<ArrowForward />}
                      fullWidth
                    >
                      View Details
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {filteredProducts.length === 0 && (
            <Box className={styles.noResults}>
              <Typography variant="h6">
                No products found in this category
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Try selecting a different category
              </Typography>
            </Box>
          )}
        </Container>
      </Box>

      {/* Product Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        className={styles.dialog}
      >
        {selectedProduct && (
          <>
            <DialogTitle className={styles.dialogTitle}>
              <Box className={styles.dialogHeader}>
                <Box
                  className={styles.dialogIcon}
                  sx={{
                    background: `${selectedProduct.color}20`,
                    color: selectedProduct.color,
                  }}
                >
                  {selectedProduct.icon}
                </Box>
                <Typography variant="h5" className={styles.dialogAppTitle}>
                  {selectedProduct.name}
                </Typography>
                <IconButton
                  onClick={handleCloseDialog}
                  className={styles.dialogClose}
                >
                  <Close />
                </IconButton>
              </Box>
              <Chip
                label={selectedProduct.category}
                size="small"
                className={styles.dialogCategoryChip}
                sx={{
                  background: `${selectedProduct.color}20`,
                  color: selectedProduct.color,
                }}
              />
            </DialogTitle>
            <DialogContent className={styles.dialogContent}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box
                    component="img"
                    src={selectedProduct.image}
                    alt={selectedProduct.name}
                    className={styles.dialogImage}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography
                    variant="body1"
                    className={styles.dialogDescription}
                  >
                    {selectedProduct.description}
                  </Typography>
                  <Divider className={styles.dialogDivider} />
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    className={styles.dialogFeaturesTitle}
                  >
                    Features:
                  </Typography>
                  <Box className={styles.dialogFeaturesList}>
                    {selectedProduct.features.map((feature, index) => (
                      <Box key={index} className={styles.dialogFeature}>
                        <CheckCircle
                          className={styles.dialogFeatureCheck}
                          sx={{ color: selectedProduct.color }}
                        />
                        <Typography variant="body2">{feature}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions className={styles.dialogActions}>
              <Button
                variant="outlined"
                onClick={handleCloseDialog}
                className={styles.dialogCancelBtn}
              >
                Close
              </Button>
              <Button
                variant="contained"
                className={styles.dialogInquiryBtn}
                startIcon={<Phone />}
              >
                Request a Call Back
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* CTA Section */}
      <Box className={styles.ctaSection}>
        <Container maxWidth="lg">
          <Box className={styles.ctaContent}>
            <Typography variant="h3" className={styles.ctaTitle}>
              Need Custom <span className={styles.highlight}>Hardware</span>{" "}
              Solutions?
            </Typography>
            <Typography variant="body1" className={styles.ctaText}>
              We specialize in developing cutting edge technology products
              tailored to your specific business needs. Let's create something
              amazing together.
            </Typography>
            <Box className={styles.ctaButtons}>
              <Button
                variant="contained"
                className={styles.ctaPrimaryBtn}
                startIcon={<Phone />}
              >
                Request a Call Back
              </Button>
              <Button
                variant="outlined"
                className={styles.ctaSecondaryBtn}
                startIcon={<Email />}
              >
                Email Us
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Hardwares;
